'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function EstetikIslemlerdeParaIadesi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/estetik-islemlerde-para-iadesi-mumkun-mu';
  const pageTitle = 'Estetik İşlemlerde Para İadesi: Mümkün mü?';
  const pageDescription = 'Estetik işlemlerde memnun kalınmazsa para iadesi yapılır mı? Tıbbi hizmetlerde iade hakkı, etik kurallar ve yasal dayanaklar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Estetik İşlemlerde Para İadesi', href: '/tr/blog/estetik-islemlerde-para-iadesi-mumkun-mu' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Estetik işlemlerde para iadesi ve hasta hakları"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Hasta Hakları & Hukuk
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Estetik İşlemlerde Para İadesi: Mümkün mü?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>25 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Estetik uygulamalarda beklenti her zaman "mükemmel sonuç"tur.
                Ancak bazı durumlarda kişi yapılan işlemden memnun kalmayabilir veya beklediği sonucu elde edemeyebilir.
                Peki bu durumda para iadesi talep etmek mümkün müdür?
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  Kısa cevap: Hayır, estetik işlemler "tıbbi müdahale" kapsamındadır; garanti veya iade şartı yoktur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Estetik İşlemler Hukuken "Tıbbi Hizmet"tir
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu, botoks, mezoterapi veya PRP gibi işlemler, Tıbbi Deontoloji Tüzüğü'ne göre birer tıbbi müdahaledir.
                Bu nedenle, "ürün satışı" değil "hizmet sunumu" sayılır.
              </p>

              <div className="bg-amber-50 rounded-xl p-6 mb-6">
                <p className="text-amber-800">
                  Yani kozmetik mağazadan alınan bir kremde olduğu gibi "memnun kalmadım, iade istiyorum" hakkı geçerli değildir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Yasal Dayanaklar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-amber-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Kanun / Yönetmelik</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Tıbbi Deontoloji Tüzüğü (Madde 13)</td>
                      <td className="border border-gray-300 px-4 py-3">Hekim, tedavi sonucunu garanti edemez.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hasta Hakları Yönetmeliği (Madde 20)</td>
                      <td className="border border-gray-300 px-4 py-3">Hasta, bilgilendirilmiş onam verir ve riskleri kabul eder.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Tüketici Kanunu (Madde 2)</td>
                      <td className="border border-gray-300 px-4 py-3">Tıbbi hizmetler, "ticari mal" değil, "sağlık hizmeti"dir.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yargıtay Kararı (E.2019/841)</td>
                      <td className="border border-gray-300 px-4 py-3">"Estetik işlem sonrası iade talebi geçerli değildir."</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. İade Yerine Ne Yapılır?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hekimler, memnuniyet sağlamak için düzeltme seansı (revizyon) yapabilir.
                Bu genellikle ücretsizdir ve hasta memnuniyetini artırmak amacı taşır.
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  Örneğin:
                </p>
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Dolgu sonrası asimetri varsa ek enjeksiyon,</li>
                  <li>Botoksta kas dengesizliği varsa rötuş yapılabilir.</li>
                </ul>
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-900 font-medium">
                    Ancak bu iyi niyet uygulamasıdır, yasal zorunluluk değildir.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Hasta-Hekim Arasındaki Sözleşme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Her estetik işlem öncesinde hastaya bilgilendirilmiş onam formu imzalatılır.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 mb-4 font-medium">
                  Bu formda:
                </p>
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>İşlemin amacı,</li>
                  <li>Olası riskler,</li>
                  <li>Beklenen sonuç,</li>
                  <li>Garanti verilmeyeceği açıkça belirtilir.</li>
                </ul>
                <div className="bg-blue-100 rounded-lg p-4 mt-4">
                  <p className="text-blue-900 font-medium">
                    Bu belge, hem hekimi hem hastayı korur.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Estetikte "Başarı" Nasıl Ölçülür?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Başarı yalnızca teknik değil, algısal bir kavramdır.
                Aynı işlem bir hastada mükemmel sonuç verirken diğerinde beklentiyi karşılamayabilir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  💬 Estetikte "mutlak doğru" değil, "kişisel uyum" vardır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Para İadesi Yapılan İstisnai Durumlar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-orange-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">İade Mümkün mü?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ürün sahte veya bozuksa</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <span className="text-green-600 font-bold">✅ Evet</span>
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">İşlem hekim değil, yetkisiz kişi tarafından yapıldıysa</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <span className="text-green-600 font-bold">✅ Evet</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ciddi tıbbi komplikasyon geliştiyse</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <span className="text-yellow-600 font-bold">⚖️ Hukuki süreç gerekir</span>
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kişisel memnuniyetsizlik</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <span className="text-red-600 font-bold">❌ Hayır</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Hasta Hakları ve Yükümlülükleri
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h4 className="font-semibold text-green-900 mb-4 flex items-center">
                    <span className="text-green-600 mr-2">✅</span>
                    Hasta Hakları
                  </h4>
                  <ul className="space-y-2 text-green-800">
                    <li>• Bilgilendirilmiş onam hakkı</li>
                    <li>• Revizyon seansı talep etme</li>
                    <li>• İkinci görüş alma</li>
                    <li>• Şikayet hakkı</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 rounded-xl p-6">
                  <h4 className="font-semibold text-red-900 mb-4 flex items-center">
                    <span className="text-red-600 mr-2">⚠️</span>
                    Hasta Yükümlülükleri
                  </h4>
                  <ul className="space-y-2 text-red-800">
                    <li>• Riskleri kabul etme</li>
                    <li>• Sonucu garanti etmeme</li>
                    <li>• Hekim talimatlarına uyma</li>
                    <li>• Gerçekçi beklenti</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Estetik işlemler sonucunda elde edilen değişiklikler kişiden kişiye farklılık gösterir.
                  Hekim sonucu garanti edemez; ancak hasta memnuniyeti bizim için etik bir sorumluluktur."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. İşlemden memnun kalmadım, paramı geri alabilir miyim?</h3>
                  <p className="text-gray-600">Hayır, tıbbi hizmetlerde iade hakkı yoktur.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Dolgu eşit olmadı, ne yapılır?</h3>
                  <p className="text-gray-600">Revizyon seansı planlanabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Sahte ürün kullanıldığını fark ettim, ne yapmalıyım?</h3>
                  <p className="text-gray-600">Sağlık Bakanlığı'na ve Tüketici Hakem Heyeti'ne başvurabilirsiniz.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Revizyon seansı ücretsiz mi?</h3>
                  <p className="text-gray-600">Genellikle evet, ancak bu hekimin iyi niyet uygulamasıdır.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hukuki süreç başlatabilir miyim?</h3>
                  <p className="text-gray-600">Ciddi tıbbi hata varsa evet, ancak memnuniyetsizlik yeterli değildir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>T.C. Sağlık Bakanlığı – Hasta Hakları Yönetmeliği</li>
                  <li>Tıbbi Deontoloji Tüzüğü (Resmî Gazete, 2010)</li>
                  <li>Yargıtay Kararı (E.2019/841, K.2020/513)</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Hasta Memnuniyeti ve Revizyon Uygulamaları"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#paraİadesi</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#hastahakları</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#hukuk</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-amber-600 to-orange-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Estetik İşlemler Hakkında Danışmanlık</h3>
          <p className="mb-6">Uzman doktorumuzdan estetik işlemler ve hasta hakları hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Estetik işlemler ve hasta hakları hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/orijinal-botoks-anlama-yontemleri-guvenli-urun-tespit-etme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Botoksu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Orijinal botoks nasıl anlaşılır ve güvenli ürün nasıl tespit edilir?</p>
            </a>
            <a
              href="/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Dolguyu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Orijinal dolgu nasıl anlaşılır ve sahte ürünlerden nasıl korunulur?</p>
            </a>
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
